import { useState, useEffect } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useTonConnectUI, useTonWallet } from "@tonconnect/ui-react";
import { colors } from "../theme";
import Header from "../components/Header";
import SwipeTrack from "../components/SwipeTrack";
import ProcessingOverlay, { type ProcessingPhase } from "../components/ProcessingOverlay";
import api from "../api";
import { cleanDescription } from "../utils/cleanDescription";
import { useUser } from "../hooks/useAuth";
import { formatTimeLeft as sharedFormatTimeLeft } from "../hooks/useBets";

const JOIN_VERIFY_ATTEMPTS = 6;

interface BetData {
  bet_id: string;
  description: string;
  amount: number;
  status: string;
  challenger_name: string;
  creator_name?: string;
  creator_username?: string;
  expires_at: string;
  strava_bet_type: string | null;
  category?: string;
  bet_mode?: string;
  pile_on_type?: string;
  target_telegram_id?: number;
  total_pot?: number;
  for_total?: number;
  against_total?: number;
  for_count?: number;
  against_count?: number;
  join_window_closes_at?: string;
}

const AMOUNT_CHIPS = [0.1, 0.5, 1.0, 2.0, 5.0];

export default function JoinBet() {
  const { betId } = useParams<{ betId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [tonConnectUI] = useTonConnectUI();
  const tonWallet = useTonWallet();
  const currentUser = useUser();

  const preSelectedSide = searchParams.get("side");
  const isTargetFromUrl = searchParams.get("role") === "target";
  const [isTarget, setIsTarget] = useState(isTargetFromUrl);

  const [bet, setBet] = useState<BetData | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedSide, setSelectedSide] = useState<"for" | "against">(
    (preSelectedSide as "for" | "against") || "for"
  );
  const [selectedAmount, setSelectedAmount] = useState(0.1);
  const [tonSent, setTonSent] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tonPrice, setTonPrice] = useState<number | null>(null);
  // Phase drives the full-screen ProcessingOverlay so the user can't
  // tap through / hit back during wallet connect, tx signing, or the
  // post-tx verification retry loop.
  const [phase, setPhase] = useState<ProcessingPhase>("idle");
  const [verifyAttempt, setVerifyAttempt] = useState(0);

  // VEN-38: tick every second so the Join screen countdown stays live.
  const [nowTick, setNowTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setNowTick((n) => n + 1), 1_000);
    return () => clearInterval(id);
  }, []);
  // Silence unused-var warning — nowTick's only job is forcing re-render.
  void nowTick;

  // VEN-75: auto-dismiss the "Connecting wallet…" overlay the moment a
  // wallet is actually connected. tonConnectUI.openModal() resolves when
  // the modal closes (which on mobile happens via redirect, *before* the
  // wallet has reported back), so relying on the await alone would leave
  // the overlay stuck.
  useEffect(() => {
    if (phase === "connecting" && tonWallet) {
      setPhase("idle");
      setError("Wallet connected \u2014 swipe again to join");
    }
  }, [phase, tonWallet]);

  // Fetch bet data
  useEffect(() => {
    if (!betId) {
      setLoading(false);
      setLoadError("No bet ID");
      return;
    }

    let cancelled = false;

    async function loadBet() {
      // Retry up to 3 times with increasing delays (auth may not be ready)
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          const res = await api.get(`/bets/${betId}`, { timeout: 10000 });
          if (cancelled) return;
          setBet(res.data);
          return;
        } catch (err: any) {
          if (cancelled) return;
          if (err.response?.status === 401 && attempt < 2) {
            await new Promise((r) => setTimeout(r, 1500 * (attempt + 1)));
            continue;
          }
          if (!cancelled) {
            setLoadError(err.response?.data?.detail || "Failed to load bet");
          }
          return;
        }
      }
      if (!cancelled) setLoadError("Failed to load bet after retries");
    }

    loadBet().finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [betId]);

  // Redirect if user already joined this bet
  useEffect(() => {
    if (!bet || !currentUser || !betId) return;
    if (bet.bet_mode !== "pile_on") return;

    api.get(`/bets/${betId}/participants`).then((res) => {
      const data = res.data;
      if (!data) return;
      const allParticipants = [...(data.for_side || []), ...(data.against_side || [])];
      const alreadyJoined = allParticipants.some(
        (p: any) => p.telegram_id === currentUser.telegram_id
      );
      if (alreadyJoined) {
        navigate(`/bet/${betId}`, { replace: true });
      }
    }).catch(() => {});
  }, [bet, currentUser, betId, navigate]);

  // Fetch TON price
  useEffect(() => {
    api
      .get("/crypto/price/toncoin")
      .then((res) => {
        const price = res.data?.price || res.data?.toncoin?.usd;
        if (typeof price === "number") setTonPrice(price);
      })
      .catch(() => setTonPrice(1.34));
  }, []);

  // VEN-113: auto-detect target role from bet data when the URL param is
  // missing (e.g. user navigated via a generic bet_ deep link instead of
  // the vs_ or dare_ deep link).
  useEffect(() => {
    if (!bet || !currentUser) return;
    const isDareBet = bet.pile_on_type === "dare";
    const isVersusBet = bet.pile_on_type === "versus";
    if (
      !isTargetFromUrl &&
      (isDareBet || isVersusBet) &&
      bet.target_telegram_id &&
      currentUser.telegram_id === bet.target_telegram_id
    ) {
      setIsTarget(true);
    }
  }, [bet, currentUser, isTargetFromUrl]);

  // Force side + amount for dare/versus targets
  useEffect(() => {
    if (isTarget && bet) {
      if (bet.pile_on_type === "dare") {
        setSelectedSide("for");
        const creatorStake = bet.amount ? bet.amount / 1_000_000_000 : 0;
        if (creatorStake <= 0) {
          // Don't silently default — this shouldn't happen
          setSelectedAmount(0.1);
        } else {
          setSelectedAmount(creatorStake);
        }
      }
      if (bet.pile_on_type === "versus") {
        setSelectedSide("against");
        // VEN-113: versus target matches the creator's stake — no amount picker
        const creatorStake = bet.amount ? bet.amount / 1_000_000_000 : 0;
        if (creatorStake <= 0) {
          setSelectedAmount(0.1);
        } else {
          setSelectedAmount(creatorStake);
        }
      }
    }
  }, [isTarget, bet]);

  if (loading) {
    return (
      <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Header title="JOIN BET" showClose />
        <div
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: colors.grayLight,
          }}
        >
          Loading...
        </div>
      </div>
    );
  }

  if (loadError || !bet) {
    return (
      <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Header title="JOIN BET" showClose />
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: 32,
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: 48, marginBottom: 16 }}>{"\u26A0\uFE0F"}</div>
          <div style={{ fontSize: 16, color: colors.danger, marginBottom: 8 }}>
            {loadError || "Bet not found"}
          </div>
          <div style={{ fontSize: 14, color: colors.grayLight }}>
            This bet may have expired or been cancelled
          </div>
        </div>
      </div>
    );
  }

  const creatorName = (
    bet.challenger_name ||
    bet.creator_name ||
    "Someone"
  ).replace(/^@/, "");
  const cleanedDescription = cleanDescription(bet.description);
  const isFitness = bet.strava_bet_type !== null;
  const forTotal = (bet.for_total || 0) / 1_000_000_000;
  const againstTotal = (bet.against_total || 0) / 1_000_000_000;
  const forCount = bet.for_count || 0;
  const againstCount = bet.against_count || 0;
  const fiatAmount = tonPrice
    ? (selectedAmount * tonPrice).toFixed(2)
    : (selectedAmount * 1.34).toFixed(2);

  const isDare = bet.pile_on_type === "dare";
  const isVersus = bet.pile_on_type === "versus";
  const selectedAmountLabel = selectedAmount % 1 === 0 ? selectedAmount.toFixed(0) : selectedAmount.toFixed(1);

  // Join window countdown
  const joinWindowLeft = bet.join_window_closes_at
    ? formatTimeLeft(bet.join_window_closes_at)
    : null;

  const handleSwipeComplete = async () => {
    if (tonSent || phase !== "idle") return;

    // Pre-flight: wallet must be connected. Show an overlay while the
    // Tonkeeper modal / redirect is active so the user doesn't think
    // the app is frozen and doesn't try to swipe again mid-flow.
    if (!tonConnectUI.account) {
      setError(null);
      setPhase("connecting");
      try {
        await tonConnectUI.openModal();
      } catch {}
      setPhase("idle");
      if (tonConnectUI.account) {
        setError("Wallet connected \u2014 swipe again to join");
      } else {
        setError("Connect your TON wallet to join the bet");
      }
      return;
    }

    setTonSent(true);
    setProcessing(true);
    setError(null);

    try {
      // 1. Get escrow address
      const escrowRes = await api.get("/bets/escrow-address");
      // VEN-64: user-friendly form — raw `0:<hex>` blocks some Tonkeeper
      // builds from opening the wallet sheet at all (reverts VEN-21).
      const escrowAddress = escrowRes.data?.address || escrowRes.data;

      // 2. Save wallet before Tonkeeper tx
      const wallet = tonConnectUI.account;
      if (wallet?.address) {
        try {
          await api.put("/users/me/wallet", { ton_address: wallet.address, ton_address_raw: wallet.address });
        } catch {}
      }

      // 3. Send TON to escrow
      const amountNano = Math.round(selectedAmount * 1_000_000_000);
      const tx = {
        validUntil: Math.floor(Date.now() / 1000) + 300,
        messages: [
          {
            address: escrowAddress,
            amount: amountNano.toString(),
          },
        ],
      };

      setPhase("signing");
      await tonConnectUI.sendTransaction(tx);

      // 4. Retry join verification — TonCenter indexing takes ~30s so the
      // first 1-2 attempts routinely 400 with "not confirmed". Overlay
      // reports progress so the user sees forward motion.
      setPhase("verifying");
      for (let attempt = 0; attempt < JOIN_VERIFY_ATTEMPTS; attempt++) {
        setVerifyAttempt(attempt + 1);
        try {
          await api.post(`/bets/${bet.bet_id}/join`, {
            side: selectedSide,
            amount: amountNano,
          });
          break;
        } catch (e: any) {
          if (
            e.response?.status === 400 &&
            e.response?.data?.detail?.includes("not confirmed") &&
            attempt < JOIN_VERIFY_ATTEMPTS - 1
          ) {
            await new Promise((r) => setTimeout(r, 8000));
            continue;
          }
          throw e;
        }
      }

      setSuccess(true);
      setProcessing(false);
      setPhase("success");

      setTimeout(() => navigate(`/bet/${bet.bet_id}`), 1500);
    } catch (err) {
      setProcessing(false);
      setPhase("idle");
      setVerifyAttempt(0);
      const msg = err instanceof Error ? err.message : String(err);
      const backendDetail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;

      if (msg.includes("TON_CONNECT_SDK_ERROR") || /connect.*wallet/i.test(msg)) {
        setTonSent(false);
        setError("Connect your TON wallet to join the bet");
      } else if (
        msg.includes("cancel") ||
        msg.includes("reject") ||
        msg.includes("closed")
      ) {
        setTonSent(false);
        setError("Wallet cancelled \u2014 swipe to retry");
      } else if (backendDetail?.includes("not confirmed")) {
        setError("Deposit is taking longer than usual to confirm. Your funds are safe \u2014 please wait a minute and tap the home button to check your bets.");
      } else if (backendDetail) {
        setError(backendDetail);
      } else {
        setError(msg || "Something went wrong");
      }
    }
  };

  const sideLabel =
    selectedSide === "for" ? "JOIN FOR \uD83D\uDCAA" : "JOIN AGAINST \uD83D\uDE08";
  const swipeLabel = (isDare && isTarget)
    ? `ACCEPT DARE 🔥 — ${selectedAmountLabel} TON`
    : (isVersus && isTarget)
      ? `ACCEPT ⚔️ — ${selectedAmountLabel} TON`
      : `${sideLabel} \u2014 ${selectedAmount} TON`;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Header title="JOIN BET" showClose onClose={() => navigate("/")} />

      <ProcessingOverlay
        phase={phase}
        attempt={verifyAttempt}
        totalAttempts={JOIN_VERIFY_ATTEMPTS}
      />

      <div
        style={{
          flex: 1,
          overflow: "auto",
          padding: "0 16px 24px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Challenge header */}
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>
            {isDare ? "\uD83C\uDFAF" : isVersus ? "\u2694\uFE0F" : "\uD83D\uDD25"}
          </div>
          <div style={{ fontSize: 22, fontWeight: 800 }}>
            {isDare
              ? "You've been DARED!"
              : isVersus
                ? "VERSUS CHALLENGE!"
                : "Join the action"}
          </div>
        </div>

        {/* Bet card */}
        <div
          style={{
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: 16,
            padding: 16,
            marginBottom: 20,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginBottom: 12,
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 18,
                background: colors.purple,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 16,
                fontWeight: 700,
                color: colors.white,
              }}
            >
              {creatorName.charAt(0).toUpperCase()}
            </div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>
              @{creatorName} {"\u2014"} {cleanedDescription}
            </div>
          </div>
          <div
            style={{
              display: "flex",
              gap: 12,
              fontSize: 13,
              color: colors.grayLight,
              flexWrap: "wrap",
            }}
          >
            <span>
              {"\u23F0"} {formatTimeLeft(bet.expires_at)}
            </span>
            {isFitness && <span>{"\uD83D\uDCCA"} HealthBridge</span>}
            {joinWindowLeft && (
              <span>
                {"\uD83D\uDD50"} Join: {joinWindowLeft} left
              </span>
            )}
          </div>
        </div>

        {/* Side picker — hidden for dare/versus targets */}
        {!isTarget && (
          <>
            <div
              style={{
                textAlign: "center",
                fontSize: 14,
                fontWeight: 700,
                color: colors.grayLight,
                textTransform: "uppercase",
                marginBottom: 12,
              }}
            >
              Pick your side
            </div>

            <div
              style={{
                display: "flex",
                gap: 12,
                marginBottom: 24,
              }}
            >
              {/* FOR card */}
              <button
                onClick={() => setSelectedSide("for")}
                style={{
                  flex: 1,
                  background:
                    selectedSide === "for"
                      ? "rgba(34,197,94,0.1)"
                      : colors.surface,
                  border: `2px solid ${selectedSide === "for" ? colors.success : colors.border}`,
                  borderRadius: 16,
                  padding: 16,
                  textAlign: "center",
                  cursor: "pointer",
                }}
              >
                <div style={{ fontSize: 24, marginBottom: 4 }}>{"\uD83D\uDCAA"}</div>
                <div
                  style={{
                    fontSize: 16,
                    fontWeight: 800,
                    color: colors.success,
                    marginBottom: 4,
                  }}
                >
                  FOR
                </div>
                <div style={{ fontSize: 14, color: colors.grayLight }}>
                  {forTotal.toFixed(1)} TON
                </div>
                <div style={{ fontSize: 12, color: colors.gray }}>
                  {forCount} backer{forCount !== 1 ? "s" : ""}
                </div>
              </button>

              {/* AGAINST card */}
              <button
                onClick={() => setSelectedSide("against")}
                style={{
                  flex: 1,
                  background:
                    selectedSide === "against"
                      ? "rgba(239,68,68,0.1)"
                      : colors.surface,
                  border: `2px solid ${selectedSide === "against" ? colors.danger : colors.border}`,
                  borderRadius: 16,
                  padding: 16,
                  textAlign: "center",
                  cursor: "pointer",
                }}
              >
                <div style={{ fontSize: 24, marginBottom: 4 }}>{"\uD83D\uDE08"}</div>
                <div
                  style={{
                    fontSize: 16,
                    fontWeight: 800,
                    color: colors.danger,
                    marginBottom: 4,
                  }}
                >
                  AGAINST
                </div>
                <div style={{ fontSize: 14, color: colors.grayLight }}>
                  {againstTotal.toFixed(1)} TON
                </div>
                <div style={{ fontSize: 12, color: colors.gray }}>
                  {againstCount} backer{againstCount !== 1 ? "s" : ""}
                </div>
              </button>
            </div>
          </>
        )}

        {!((isDare || isVersus) && isTarget) && (
          <>
            {/* Amount chips */}
            <div
              style={{
                textAlign: "center",
                fontSize: 14,
                fontWeight: 700,
                color: colors.grayLight,
                textTransform: "uppercase",
                marginBottom: 12,
              }}
            >
              Your stake
            </div>

            <div
              style={{
                display: "flex",
                gap: 8,
                justifyContent: "center",
                marginBottom: 8,
              }}
            >
              {AMOUNT_CHIPS.map((amt) => (
                <button
                  key={amt}
                  onClick={() => setSelectedAmount(amt)}
                  style={{
                    padding: "10px 16px",
                    borderRadius: 12,
                    fontSize: 15,
                    fontWeight: 700,
                    border: `2px solid ${selectedAmount === amt ? colors.primary : colors.border}`,
                    background:
                      selectedAmount === amt
                        ? "rgba(173,255,0,0.1)"
                        : colors.surface,
                    color:
                      selectedAmount === amt ? colors.primary : colors.grayLight,
                    cursor: "pointer",
                  }}
                >
                  {amt} TON
                </button>
              ))}
            </div>

            <div
              style={{
                textAlign: "center",
                fontSize: 13,
                color: colors.grayLight,
                marginBottom: 24,
              }}
            >
              {"\u2248"} ${fiatAmount}
            </div>
          </>
        )}

        {(isDare || isVersus) && isTarget && (
          <div style={{ textAlign: "center", marginBottom: 24 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: colors.grayLight, textTransform: "uppercase", marginBottom: 8 }}>
              Match their stake
            </div>
            <div style={{ fontSize: 28, fontWeight: 800 }}>
              {selectedAmountLabel} TON
            </div>
            <div style={{ fontSize: 13, color: colors.grayLight, marginTop: 4 }}>
              ≈ ${fiatAmount}
            </div>
          </div>
        )}

        {/* Spacer */}
        <div style={{ flex: 1 }} />

        {/* Swipe to join */}
        <SwipeTrack
          label={swipeLabel}
          onComplete={handleSwipeComplete}
          processing={processing}
          success={success}
          error={error}
        />

        <div
          style={{
            textAlign: "center",
            fontSize: 13,
            color: colors.grayLight,
            marginTop: 12,
            paddingBottom: 8,
          }}
        >
          {isVersus && isTarget
            ? "You've been challenged. Accept and prove them wrong! \u26A1"
            : selectedSide === "for"
              ? "Believe they'll crush it? Back them! \uD83D\uDCAA"
              : "Think they'll fail? Easy money. \uD83D\uDE0F"}
        </div>
      </div>
    </div>
  );
}

// VEN-38: use the shared util so every screen agrees on the countdown.
function formatTimeLeft(dateStr: string): string {
  return sharedFormatTimeLeft(dateStr);
}
